import { useEffect, useRef, useState } from 'react';
import { Modal, Slider, Space, Typography } from 'antd';

const OUTPUT_SIZE = 256;
const FRAME_SIZE = 320;

interface Props {
  open: boolean;
  file: File | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}

interface Pan {
  x: number;
  y: number;
}

/**
 * Кроп аватарки: круглая область 320×320 на canvas, изображение можно
 * двигать мышью и масштабировать слайдером. На сохранение возвращает
 * data:image/webp с финальным размером 256×256 (≈ 20–80 KB).
 */
export function AvatarCropModal({ open, file, saving, onCancel, onConfirm }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const dragStateRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(
    null,
  );

  useEffect(() => {
    if (!open || !file) {
      imgRef.current = null;
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const fit = Math.max(FRAME_SIZE / img.width, FRAME_SIZE / img.height);
      setMinScale(fit);
      setScale(fit);
      setPan({ x: 0, y: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [open, file]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, FRAME_SIZE, FRAME_SIZE);
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, FRAME_SIZE, FRAME_SIZE);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, FRAME_SIZE / 2 - w / 2 + pan.x, FRAME_SIZE / 2 - h / 2 + pan.y, w, h);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(FRAME_SIZE / 2, FRAME_SIZE / 2, FRAME_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }, [scale, pan, open]);

  const onMouseDown = (e: React.MouseEvent) => {
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const s = dragStateRef.current;
    if (!s) return;
    setPan({ x: s.panX + (e.clientX - s.startX), y: s.panY + (e.clientY - s.startY) });
  };
  const onMouseUp = () => {
    dragStateRef.current = null;
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img) return;
    const out = document.createElement('canvas');
    out.width = OUTPUT_SIZE;
    out.height = OUTPUT_SIZE;
    const ctx = out.getContext('2d');
    if (!ctx) return;
    const k = OUTPUT_SIZE / FRAME_SIZE;
    const w = img.width * scale * k;
    const h = img.height * scale * k;
    ctx.drawImage(img, OUTPUT_SIZE / 2 - w / 2 + pan.x * k, OUTPUT_SIZE / 2 - h / 2 + pan.y * k, w, h);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    const dataUrl = out.toDataURL('image/webp', 0.9);
    onConfirm(dataUrl);
  };

  return (
    <Modal
      title="Обрезка аватарки"
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      destroyOnClose
      width={400}
    >
      <Space direction="vertical" size={12} style={{ width: '100%', alignItems: 'center' }}>
        <canvas
          ref={canvasRef}
          width={FRAME_SIZE}
          height={FRAME_SIZE}
          style={{
            cursor: dragStateRef.current ? 'grabbing' : 'grab',
            borderRadius: '50%',
            background: '#f0f0f0',
            touchAction: 'none',
            userSelect: 'none',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        />
        <div style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Масштаб
          </Typography.Text>
          <Slider
            min={minScale}
            max={minScale * 4}
            step={0.01}
            value={scale}
            onChange={(v) => setScale(typeof v === 'number' ? v : minScale)}
          />
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Перетащите изображение, чтобы выбрать фрагмент
        </Typography.Text>
      </Space>
    </Modal>
  );
}
